import { GetAllHackersQuery } from "src/api/sponsors/getAllHackers.generated";

import { THackerProfile } from "./components/HackerProfile/types";

export type THackerFilter = {
  type:
    | "school"
    | "program"
    | "starred"
    | "gradYear"
    | "scanned"
    | "interested";
  value: string[] | boolean;
};

export type TAcceptanceStatus = "all" | "pending" | "accepted" | "rejected";

const APPLICATION_STAGES_BY_STATUS = {
  pending: ["submitted_application", "under_review", "reviewed", "waitlisted"],
  accepted: ["accepted", "auto-accepted", "confirmed", "checked_in", "expired"],
  rejected: ["not_accepted"],
};

export const getApplicationStages = (status: TAcceptanceStatus): string[] =>
  status === "all"
    ? Object.values(APPLICATION_STAGES_BY_STATUS).reduce(
        (stages, statusStages) => stages.concat(statusStages),
        []
      )
    : APPLICATION_STAGES_BY_STATUS[status];

export const getAcceptanceStatus = (
  stage: string
): Exclude<TAcceptanceStatus, "all"> => {
  if (APPLICATION_STAGES_BY_STATUS.accepted.includes(stage)) return "accepted";
  if (APPLICATION_STAGES_BY_STATUS.rejected.includes(stage)) return "rejected";
  return "pending";
};

export const hackerExpressedInterestInCompany = (
  sponsorInterests: string[] | undefined,
  companyName: string | undefined
): boolean => {
  if (!companyName || !sponsorInterests?.length) return false;
  const normalizedCompanyName = companyName.toLowerCase();
  return sponsorInterests.some(
    (interest) => interest.toLowerCase() === normalizedCompanyName
  );
};

export const getFilteredHackerIds = (
  filters: THackerFilter[],
  starredHackerIds: number[],
  scannedHackerIds: number[]
): number[] | null => {
  const isStarred =
    filters.find((filter) => filter.type === "starred")?.value === true;
  const isScanned =
    filters.find((filter) => filter.type === "scanned")?.value === true;
  if (!isStarred && !isScanned) return null;
  if (isStarred && !isScanned) return starredHackerIds;
  if (!isStarred && isScanned) return scannedHackerIds;
  const starredSet = new Set(starredHackerIds);
  return scannedHackerIds.filter((id) => starredSet.has(id));
};

export const parseHackerProfiles = (
  data: GetAllHackersQuery | undefined
): THackerProfile[] =>
  data?.getSponsorRecruitmentCandidates.candidates.map((hacker) => ({
    id: hacker.id,
    name: hacker.name,
    graduatingYear: hacker.graduating_year ?? 0,
    school: hacker.school ?? undefined,
    program: hacker.program ?? undefined,
    email: hacker.email,
    links: hacker.links,
    resume: hacker.resume ?? undefined,
    tags: [],
    levelOfStudy: hacker.level_of_study ?? "",
    sponsorInterests: hacker.sponsor_interests,
    applicationStage: hacker.application_stage,
  })) ?? [];
